// MigrationDashboard.jsx - VERSIÓN COMPLETA CORREGIDA
import React, { useState, useEffect } from 'react';
import { getLegacyBridge } from '../../lib/bridges/LegacyBridge';
import './MigrationDashboard.css';

const MigrationDashboard = () => {
  const [bridge, setBridge] = useState(null);
  const [migrationStats, setMigrationStats] = useState({
    stats: {},
    totals: { legacy: 0, migrated: 0, collections: 0 },
    bridgeMode: false
  });
  const [migrationLogs, setMigrationLogs] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  const collections = [
    { name: 'clients', label: 'Clientes' },
    { name: 'properties', label: 'Imóveis' },
    { name: 'tasks', label: 'Tarefas' },
    { name: 'calendarEvents', label: 'Calendário' },
    { name: 'documents', label: 'Documentos' },
    { name: 'financeiro', label: 'Financeiro' },
    { name: 'company', label: 'Empresa' },
    { name: 'users', label: 'Usuários' }
  ];

  useEffect(() => {
    console.log('🔄 MigrationDashboard montado');
    initBridge();
  }, []);

  const initBridge = async () => {
    try {
      console.log('🔄 Inicializando LegacyBridge...');
      const bridgeInstance = getLegacyBridge();
      
      // NO llamar a initialize() - LegacyBridge se inicializa en el constructor
      setBridge(bridgeInstance);
      console.log('✅ Bridge inicializado correctamente');
      
      // Cargar estadísticas
      const stats = await bridgeInstance.getMigrationStats();
      setMigrationStats(stats);
      setIsInitialized(true);
      
      addToLogs('✅ Bridge inicializado y listo para migración', 'success');
      
    } catch (error) {
      console.error('❌ Error inicializando bridge:', error);
      addToLogs(`❌ Error inicializando bridge: ${error.message}`, 'error');
    }
  };

  const addToLogs = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setMigrationLogs(prev => [
      { message: `[${timestamp}] ${message}`, type },
      ...prev.slice(0, 19) // Mantener solo últimos 20 logs
    ]);
  };

  const handleMigrate = async (collectionName) => {
    if (!bridge || loading) return;
    
    setLoading(true);
    addToLogs(`🚀 Iniciando migración de: ${collectionName}...`, 'info');
    
    try {
      // Obtener transformador para esta colección
      const transformers = bridge.getDefaultTransformers();
      const transformer = transformers[collectionName] || transformers.clients;
      
      // Ejecutar migración
      const result = await bridge.migrateCollection(collectionName, transformer);
      
      if (result.success) {
        addToLogs(`✅ ${collectionName}: ${result.count} registros migrados exitosamente`, 'success');
      } else {
        addToLogs(`⚠️ ${collectionName}: Migración con ${result.errors.length} errores`, 'warning');
      }
      
      // Actualizar estadísticas
      const stats = await bridge.getMigrationStats();
      setMigrationStats(stats);
      
    } catch (error) {
      addToLogs(`❌ Error migrando ${collectionName}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (collectionName) => {
    if (!bridge || loading) return;
    
    if (!window.confirm(`¿Estás seguro de revertir la migración de ${collectionName}?`)) {
      return;
    }
    
    setLoading(true);
    addToLogs(`↩️ Revertiendo migración de: ${collectionName}...`, 'info');
    
    try {
      // Ejecutar reversión
      const result = await bridge.revertMigration(collectionName);
      
      addToLogs(`✅ ${collectionName}: Migración revertida correctamente`, 'success');
      
      // Actualizar estadísticas
      const stats = await bridge.getMigrationStats();
      setMigrationStats(stats);
      
    } catch (error) {
      addToLogs(`❌ Error revirtiendo ${collectionName}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    if (!bridge) return;
    
    try {
      const stats = await bridge.getMigrationStats();
      setMigrationStats(stats);
      addToLogs('🔄 Estado actualizado correctamente', 'info');
    } catch (error) {
      addToLogs(`❌ Error actualizando estado: ${error.message}`, 'error');
    }
  };

  const getCollectionStatus = (collectionName) => {
    const collectionStats = migrationStats.stats[collectionName];
    if (!collectionStats) return { count: 0, migrated: false, status: 'LEGACY' };
    
    return {
      count: collectionStats.count || 0,
      migrated: collectionStats.migrated || false,
      status: collectionStats.status || 'LEGACY',
      canMigrate: collectionStats.count > 0 && !collectionStats.migrated
    };
  };

  if (!isInitialized) {
    return (
      <div className="migration-dashboard loading">
        <div className="spinner"></div>
        <p>Inicializando sistema de migración...</p>
        <p className="loading-sub">Verificando datos legacy...</p>
      </div>
    );
  }

  // Calcular estadísticas generales
  const modernCollections = Object.values(migrationStats.stats).filter(s => s.migrated).length;
  const legacyCollections = Object.values(migrationStats.stats).filter(s => !s.migrated).length;
  const successfulMigrations = migrationLogs.filter(log => log.type === 'success').length;
  const errors = migrationLogs.filter(log => log.type === 'error').length;

  return (
    <div className="migration-dashboard">
      <header className="dashboard-header">
        <h1>🏗️ Migration Dashboard</h1>
        <p className="subtitle">Migração segura e controlada</p>
        
        <div className="mode-indicator">
          <span className={`mode-badge ${migrationStats.bridgeMode ? 'active' : 'inactive'}`}>
            {migrationStats.bridgeMode ? '🔁 Usando Bridge' : '⚡ Modo Directo'}
          </span>
        </div>
        
        <div className="stats-container">
          <div className="stat-card">
            <span className="stat-number">{modernCollections}</span>
            <span className="stat-label">Coleções Modernas</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{legacyCollections}</span>
            <span className="stat-label">Coleções Legacy</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{successfulMigrations}</span>
            <span className="stat-label">Migrações Bem-sucedidas</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{errors}</span>
            <span className="stat-label">Erros</span>
          </div>
        </div>
        
        <div className="total-stats">
          <p>📊 Total: {migrationStats.totals.legacy} registros legacy | {migrationStats.totals.migrated} migrados</p>
        </div>
      </header>

      <div className="collections-grid">
        {collections.map(collection => {
          const status = getCollectionStatus(collection.name);
          
          return (
            <div key={collection.name} className="collection-card">
              <div className="collection-header">
                <h3>{collection.label}</h3>
                <span className={`status-badge ${status.migrated ? 'modern' : 'legacy'}`}>
                  {status.status}
                </span>
              </div>
              
              <div className="collection-meta">
                <p className="record-count">📊 {status.count} registros</p>
                <p className="migration-status">
                  {status.migrated ? '✅ Migrado' : '⏳ Pendiente'}
                </p>
              </div>
              
              <div className="collection-actions">
                {status.canMigrate ? (
                  <button
                    onClick={() => handleMigrate(collection.name)}
                    disabled={loading}
                    className="btn-migrate"
                  >
                    {loading ? 'Migrando...' : 'Migrate to Modern'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleRevert(collection.name)}
                    disabled={!status.migrated || loading}
                    className="btn-revert"
                  >
                    {loading ? 'Revertendo...' : 'Revert Last Migration'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="logs-section">
        <div className="logs-header">
          <h3>📋 Migration Logs</h3>
          <div className="logs-actions">
            <button 
              className="btn-secondary small"
              onClick={refreshStatus}
            >
              🔄 Refresh
            </button>
            <button 
              className="btn-secondary small"
              onClick={() => setMigrationLogs([])}
            >
              🗑️ Clear
            </button>
          </div>
        </div>
        
        <div className="logs-container">
          {migrationLogs.length > 0 ? (
            migrationLogs.map((log, index) => (
              <div key={index} className={`log-entry ${log.type}`}>
                <span className="log-type">
                  {log.type === 'success' ? '✅' : 
                   log.type === 'error' ? '❌' : 
                   log.type === 'warning' ? '⚠️' : 'ℹ️'}
                </span>
                <span className="log-message">{log.message}</span>
              </div>
            ))
          ) : (
            <div className="log-entry info">
              <span className="log-type">ℹ️</span>
              <span className="log-message">No hay logs de migración aún</span>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-footer">
        <div className="footer-info">
          <p>🔒 <strong>Sistema 100% reversible</strong> - Puedes revertir cualquier migración</p>
          <p>📈 <strong>Modo Bridge activado:</strong> {migrationStats.bridgeMode ? 'SÍ' : 'NO'}</p>
        </div>
        
        <div className="footer-actions">
          <button 
            className="btn-secondary"
            onClick={() => {
              localStorage.setItem('use_legacy_bridge', 'true');
              addToLogs('🔁 Modo Bridge activado manualmente', 'info');
              refreshStatus();
            }}
          >
            Activar Bridge
          </button>
          
          <button 
            className="btn-primary"
            onClick={() => {
              const allCollections = collections.map(c => c.name);
              addToLogs('🔄 Actualizando estado de todas las colecciones...', 'info');
              refreshStatus();
            }}
          >
            Actualizar Todo
          </button>
        </div>
      </div>
    </div>
  );
};

export default MigrationDashboard;